import { PlatformEarningsDetails } from '@/components/reports/PlatformEarningsBreakdown'
import CondolencesIcon from '@/assets/CondolencesIcon'
import FsnNetworkIcon from '@/assets/FsnNetworkIcon'
import ReportDateRangeFilterBar, {
  type ReportDateRangePreset,
} from '@/components/reports/ReportDateRangeFilterBar'
import { InfoCard } from '@/components/contracts/InfoCard'
import { InfoNotice } from '@/components/patterns/info-notice'
import { AdminHeaderActionButton, AdminPageHeader } from '@/components/patterns/admin-page'
import {
  getIndexPaginationMeta,
  IndexDataTable,
  IndexTablePagination,
  IndexTableSearchBox,
  IndexTableToolbar,
} from '@/components/tables/IndexTable'
import { useIndexTableSearchParams } from '@/components/tables/useIndexTableSearchParams'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Typography } from '@/components/ui/typography'
import { usePathHelper } from '@/hooks/usePathHelper'
import { adminTitleHead } from '@/lib/admin-route-title'
import { isGlobalOrganization } from '@/lib/user-access'
import { getOrganizationNow } from '@/lib/reporting-dates'
import {
  organizationQueries,
  useOrganization,
} from '@/lib/services/organizations/organizations.query'
import { prefetchIndexTableRouteQuery } from '@/lib/services/index-table-query-options'
import type {
  PlatformEarningsSummary,
  ProcessingFeeCoverage,
} from '@/lib/services/platform-ledger/platform-ledger.types'
import {
  FLORIST_PERFORMANCE_MOMENTUM_METRIC,
  FLORIST_PERFORMANCE_SORT,
  FLORIST_PERFORMANCE_STATUS,
  FLORIST_PERFORMANCE_VIEW,
  floristPerformanceReportSearchSchema,
  getDefaultFloristPerformanceReportFilters,
  getFloristPerformanceReportFiltersFromSearch,
  getFloristPerformanceReportSearchParams,
  type FloristPerformanceReportFilters,
} from '@/lib/services/reports/reports.schema'
import type {
  FloristPerformanceReportComparisonCoverage,
  FloristPerformanceReportDefinitions,
  FloristPerformanceReportMomentumSummary,
  FloristPerformanceReportOrderCounts,
  FloristPerformanceReportSourceAmounts,
  FloristPerformanceReportSummary,
} from '@/lib/services/reports/reports.types'
import {
  reportQueries,
  useExportFloristPerformanceReportMutation,
  useFloristPerformanceReport,
} from '@/lib/services/reports/reports.query'
import { formatCurrency, formatNumber } from '@/lib/utils'
import { createFileRoute, notFound, useNavigate } from '@tanstack/react-router'
import {
  Banknote,
  CalendarRange,
  Download,
  Gauge,
  Info,
  ReceiptText,
  ShoppingBag,
  TrendingUp,
} from 'lucide-react'
import type { ReactNode } from 'react'
import { toast } from 'sonner'
import { downloadExportedReport, getExportErrorMessage } from '../-helpers'
import { getFloristPerformanceAmounts, getFloristPerformanceColumns } from './-columns'

type View = FloristPerformanceReportFilters['view']
type MomentumMetric = FloristPerformanceReportFilters['momentum_metric']

const VIEW_OPTIONS = [
  { value: FLORIST_PERFORMANCE_VIEW.ORDERS, label: 'Order Count' },
  { value: FLORIST_PERFORMANCE_VIEW.CUSTOMER_SALES, label: 'Payments Collected' },
  { value: FLORIST_PERFORMANCE_VIEW.PLATFORM_REVENUE, label: 'Platform Revenue' },
  { value: FLORIST_PERFORMANCE_VIEW.MOMENTUM, label: 'Recent Sales Trend' },
]

const STATUS_OPTIONS = [
  { value: FLORIST_PERFORMANCE_STATUS.LIVE, label: 'Live florists' },
  { value: FLORIST_PERFORMANCE_STATUS.NEEDS_SETUP, label: 'Needs setup' },
  { value: FLORIST_PERFORMANCE_STATUS.LOST, label: 'Lost florists' },
  { value: FLORIST_PERFORMANCE_STATUS.ALL, label: 'All florists' },
]

const MOMENTUM_OPTIONS = [
  { value: FLORIST_PERFORMANCE_MOMENTUM_METRIC.ORDERS, label: 'Order Count' },
  { value: FLORIST_PERFORMANCE_MOMENTUM_METRIC.CUSTOMER_SALES, label: 'Payments Collected' },
  {
    value: FLORIST_PERFORMANCE_MOMENTUM_METRIC.PLATFORM_REVENUE,
    label: 'Platform Revenue',
  },
]

export const Route = createFileRoute('/_authenticated/$orgId/reports/florist-performance/')({
  component: FloristPerformanceReportPage,
  head: () => adminTitleHead('Florist Performance', 'Reports'),
  validateSearch: (search) => floristPerformanceReportSearchSchema.parse(search),
  loaderDeps: ({ search }) => ({ search }),
  loader: async ({ context, deps: { search }, params }) => {
    if (!context.ability.can('list', 'transactions') || !context.ability.can('access', 'global')) {
      throw notFound()
    }

    const organization =
      context.queryClient.getQueryData?.(organizationQueries.show(params.orgId).queryKey) ??
      (await context.queryClient.ensureQueryData(organizationQueries.show(params.orgId)))

    if (!isGlobalOrganization(organization)) throw notFound()

    const filters = getReportFilters(search, organization?.timezone)

    void prefetchIndexTableRouteQuery(
      context.queryClient,
      reportQueries.floristPerformance({
        organizationId: params.orgId,
        filters,
      })
    )

    return { isReadOnly: true }
  },
})

export function FloristPerformanceReportPage() {
  const navigate = useNavigate()
  const path = usePathHelper()
  const { orgId } = Route.useParams()
  const search = Route.useSearch()
  const { data: organization } = useOrganization(orgId)
  const now = getOrganizationNow(organization?.timezone)
  const filters = getReportFilters(search, organization?.timezone)
  const { data: report, isFetching, isPending } = useFloristPerformanceReport(filters)
  const exportMutation = useExportFloristPerformanceReportMutation()
  const rows = report?.rows ?? []
  const meta = getIndexPaginationMeta(report, filters.page, filters.page_size)
  const { updateSearchParams, updateSearchTerm } = useIndexTableSearchParams({
    navigate,
    getSearchParams: (previous) =>
      getFloristPerformanceReportSearchParams(getReportFilters(previous, organization?.timezone)),
    searchTerm: filters.search ?? '',
  })
  const columns = getFloristPerformanceColumns({
    view: filters.view,
    momentumMetric: filters.momentum_metric,
  })
  const completedThrough = now.subtract(1, 'day').format('YYYY-MM-DD')
  const dateBounds = {
    minDate: report?.date_bounds?.min_date ?? filters.start_date,
    maxDate:
      filters.view === FLORIST_PERFORMANCE_VIEW.MOMENTUM
        ? completedThrough
        : (report?.date_bounds?.max_date ?? now.format('YYYY-MM-DD')),
  }

  const handleExport = async () => {
    try {
      const response = await exportMutation.mutateAsync({
        organizationId: orgId,
        filters,
      })

      const dateLabel =
        filters.view === FLORIST_PERFORMANCE_VIEW.MOMENTUM
          ? `as-of-${filters.end_date}`
          : `${filters.start_date}-to-${filters.end_date}`
      const viewSlug =
        filters.view === FLORIST_PERFORMANCE_VIEW.MOMENTUM
          ? `recent-sales-trend-${filters.momentum_metric.replaceAll('_', '-')}`
          : filters.view

      downloadExportedReport(response, `florist-performance-${viewSlug}-${dateLabel}.xlsx`)
    } catch (error) {
      toast.error(await getExportErrorMessage(error, 'Failed to export florist performance'))
    }
  }

  return (
    <div className="space-y-4 pb-6">
      <AdminPageHeader
        icon={TrendingUp}
        title="Florist Performance"
        description="Compare sales activity across florist organizations and identify recent sales trends."
        className="mb-4 mt-2"
        actions={
          <div className="flex flex-wrap items-center gap-2 sm:justify-end">
            <ReportDateRangeFilterBar
              initialFilters={{
                start_date: filters.start_date,
                end_date: filters.end_date,
                ...dateBounds,
              }}
              now={now}
              additionalPresets={getDatePresets(dateBounds.minDate)}
              presetKeys={
                filters.view === FLORIST_PERFORMANCE_VIEW.MOMENTUM
                  ? []
                  : [
                      'this_month',
                      'last_month',
                      'this_year',
                      'last_year',
                      'trailing_7',
                      'trailing_30',
                      'trailing_90',
                      'all_time',
                    ]
              }
              showSingleDayAsRange={filters.view !== FLORIST_PERFORMANCE_VIEW.MOMENTUM}
              singleDate={filters.view === FLORIST_PERFORMANCE_VIEW.MOMENTUM}
              onApply={({ start_date, end_date }) =>
                updateSearchParams({
                  ...(filters.view === FLORIST_PERFORMANCE_VIEW.MOMENTUM
                    ? {
                        start_date: end_date,
                        end_date,
                      }
                    : { start_date, end_date }),
                  page: 1,
                })
              }
            />

            <AdminHeaderActionButton
              type="button"
              variant="outline"
              size="lg"
              onClick={handleExport}
              disabled={!meta.total || exportMutation.isPending}
            >
              <Download className="size-4" />
              Export Spreadsheet
            </AdminHeaderActionButton>
          </div>
        }
      />

      <Tabs
        value={filters.view}
        onValueChange={(view) => {
          const trendDate =
            filters.end_date > completedThrough ? completedThrough : filters.end_date

          updateSearchParams({
            view,
            ...(view === FLORIST_PERFORMANCE_VIEW.MOMENTUM
              ? { start_date: trendDate, end_date: trendDate }
              : {}),
            sort:
              view === FLORIST_PERFORMANCE_VIEW.MOMENTUM
                ? FLORIST_PERFORMANCE_SORT.CURRENT_30_DESC
                : FLORIST_PERFORMANCE_SORT.TOTAL_DESC,
            page: 1,
          })
        }}
      >
        <TabsList aria-label="Florist performance view" layout="wrap" size="comfortable">
          {VIEW_OPTIONS.map((option) => (
            <TabsTrigger key={option.value} value={option.value}>
              {option.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <SummaryCards
        view={filters.view}
        momentumMetric={filters.momentum_metric}
        summary={report?.summary}
        earnings={report?.platform_earnings}
        coverage={report?.processing_cost_coverage}
        comparisonCoverage={report?.comparison_coverage}
      />

      <DefinitionNotice view={filters.view} definitions={report?.definitions} />

      <Card>
        <CardHeader>
          <CardTitle>{viewLabel(filters.view)}</CardTitle>
          <CardDescription>{tableDescription(filters)}</CardDescription>
        </CardHeader>
        <CardContent>
          <IndexDataTable
            data={rows}
            columns={columns}
            sort={filters.sort}
            onSortChange={(sort) => updateSearchParams({ sort, page: 1 })}
            getRowId={(row) => String(row.organization.id)}
            rowLink={(row) => path.organizations.show(row.organization.id).using({ orgId }).link}
            isLoading={isPending}
            toolbar={
              <IndexTableToolbar
                primary={
                  <IndexTableSearchBox
                    value={filters.search ?? ''}
                    onCommit={updateSearchTerm}
                    countText={floristCountText(meta.total)}
                    isFetching={isFetching}
                    placeholder="Search florists..."
                  />
                }
                secondary={
                  <>
                    <StatusSelect
                      value={filters.status}
                      onValueChange={(status) => updateSearchParams({ status, page: 1 })}
                    />
                    {filters.view === FLORIST_PERFORMANCE_VIEW.MOMENTUM ? (
                      <MomentumMetricSelect
                        value={filters.momentum_metric}
                        onValueChange={(momentumMetric) =>
                          updateSearchParams({
                            momentum_metric: momentumMetric,
                            sort: FLORIST_PERFORMANCE_SORT.CURRENT_30_DESC,
                            page: 1,
                          })
                        }
                      />
                    ) : null}
                  </>
                }
              />
            }
            emptyState={
              <Typography variant="supporting">
                {filters.search
                  ? 'No florists match your search.'
                  : 'No florist performance activity matches these filters.'}
              </Typography>
            }
            tableClassName="text-sm"
          />

          <IndexTablePagination
            meta={meta}
            pageSize={filters.page_size}
            onPageChange={(page) => updateSearchParams({ page })}
            onPageSizeChange={(pageSize) => updateSearchParams({ page_size: pageSize, page: 1 })}
          />
        </CardContent>
      </Card>
    </div>
  )
}

export function getReportFilters(
  search: unknown,
  organizationTimezone: string | undefined
): FloristPerformanceReportFilters {
  const now = getOrganizationNow(organizationTimezone)
  const view =
    floristPerformanceReportSearchSchema.parse(search).view ?? FLORIST_PERFORMANCE_VIEW.ORDERS
  const filters = getFloristPerformanceReportFiltersFromSearch(
    search,
    getDefaultFloristPerformanceReportFilters(now, view)
  )

  if (filters.view !== FLORIST_PERFORMANCE_VIEW.MOMENTUM) return filters

  const completedThrough = now.subtract(1, 'day').format('YYYY-MM-DD')
  const endDate = filters.end_date > completedThrough ? completedThrough : filters.end_date

  return { ...filters, start_date: endDate, end_date: endDate }
}

type SummaryCardsProps = {
  view: View
  momentumMetric: MomentumMetric
  summary: FloristPerformanceReportSummary | undefined
  earnings: PlatformEarningsSummary | undefined
  coverage: ProcessingFeeCoverage | undefined
  comparisonCoverage: FloristPerformanceReportComparisonCoverage | undefined
}

function SummaryCards({
  view,
  momentumMetric,
  summary,
  earnings,
  coverage,
  comparisonCoverage,
}: SummaryCardsProps) {
  if (view === FLORIST_PERFORMANCE_VIEW.ORDERS) {
    const metrics: Partial<FloristPerformanceReportOrderCounts> =
      summary && 'orders' in summary ? summary.orders : {}

    return (
      <SummaryGrid className="@[52rem]/report-summary:grid-cols-3 @[76rem]/report-summary:grid-cols-6">
        <InfoCard
          icon={ShoppingBag}
          tone="periwinkle"
          title="Total Order Count"
          value={formatNumber(metrics.total)}
        />
        <InfoCard
          icon={ReceiptText}
          tone="mint"
          title="Web Order Count"
          value={formatNumber(metrics.web)}
        />
        <InfoCard
          icon={CalendarRange}
          tone="amethyst"
          title="Subscription Order Count"
          value={formatNumber(metrics.subscription)}
        />
        <InfoCard
          icon={ReceiptText}
          tone="neutral"
          title="POS Order Count"
          value={formatNumber(metrics.pos)}
        />
        <InfoCard
          icon={FsnNetworkIcon}
          tone="mint"
          title="FSN Wire-In Order Count"
          value={formatNumber(metrics.fsn)}
        />
        <InfoCard
          icon={CondolencesIcon}
          tone="neutral"
          title="Condolences Order Count"
          value={formatNumber(metrics.condolences)}
        />
      </SummaryGrid>
    )
  }

  if (view === FLORIST_PERFORMANCE_VIEW.MOMENTUM) {
    const metrics: Partial<FloristPerformanceReportMomentumSummary['momentum']> =
      summary && 'momentum' in summary ? summary.momentum : {}
    const format = (value: number | string | undefined) =>
      formatMomentumValue(value, momentumMetric)

    return (
      <SummaryGrid>
        <InfoCard
          icon={Gauge}
          tone="periwinkle"
          title="Current 30-Day Total"
          titleEnd={
            <PlatformEarningsDetails summary={earnings} coverage={comparisonCoverage?.current_30} />
          }
          value={format(metrics.current_30_total)}
        />
        <InfoCard
          icon={CalendarRange}
          tone="neutral"
          title="Prior 30-Day Total"
          titleEnd={<PlatformEarningsDetails coverage={comparisonCoverage?.prior_30} />}
          value={format(metrics.prior_30_total)}
        />
        <InfoCard
          icon={TrendingUp}
          tone="mint"
          title="Current 7-Day Total"
          titleEnd={<PlatformEarningsDetails coverage={comparisonCoverage?.current_7} />}
          value={format(metrics.current_7_total)}
        />
        <InfoCard
          icon={CalendarRange}
          tone="neutral"
          title="Prior 7-Day Total"
          titleEnd={<PlatformEarningsDetails coverage={comparisonCoverage?.prior_7} />}
          value={format(metrics.prior_7_total)}
        />
      </SummaryGrid>
    )
  }

  const metrics: Partial<FloristPerformanceReportSourceAmounts> =
    (summary && getFloristPerformanceAmounts(summary, view)) ?? {}

  return (
    <SummaryGrid
      className={
        view === FLORIST_PERFORMANCE_VIEW.PLATFORM_REVENUE
          ? '@[52rem]/report-summary:grid-cols-3 @[76rem]/report-summary:grid-cols-6'
          : undefined
      }
    >
      <InfoCard
        icon={Banknote}
        tone="mint"
        title="Total"
        value={formatCurrency(metrics.total)}
        titleEnd={<PlatformEarningsDetails summary={earnings} coverage={coverage} />}
      />
      <InfoCard
        icon={ReceiptText}
        tone="periwinkle"
        title="Web"
        value={formatCurrency(metrics.web)}
      />
      {view === FLORIST_PERFORMANCE_VIEW.PLATFORM_REVENUE && (
        <InfoCard
          icon={Banknote}
          tone="mint"
          title="Merchant Fees"
          value={
            (earnings?.has_reportable_merchant_fees ?? earnings?.has_v2_activity)
              ? formatCurrency(earnings.merchant_fee_margin)
              : '—'
          }
          titleEnd={
            earnings?.has_v2_activity ? (
              <PlatformEarningsDetails summary={earnings} coverage={coverage} merchantOnly />
            ) : null
          }
        />
      )}
      <InfoCard
        icon={CalendarRange}
        tone="amethyst"
        title="Subscription"
        value={formatCurrency(metrics.subscription)}
      />
      <InfoCard icon={ReceiptText} tone="neutral" title="POS" value={formatCurrency(metrics.pos)} />
      <InfoCard icon={Info} tone="neutral" title="Other" value={formatCurrency(metrics.other)} />
    </SummaryGrid>
  )
}

function SummaryGrid({
  children,
  className = '@[52rem]/report-summary:grid-cols-3 @[68rem]/report-summary:grid-cols-5',
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <div className="@container/report-summary">
      <div className={`grid grid-cols-1 gap-4 @[32rem]/report-summary:grid-cols-2 ${className}`}>
        {children}
      </div>
    </div>
  )
}

function DefinitionNotice({
  view,
  definitions,
}: {
  view: View
  definitions: FloristPerformanceReportDefinitions | undefined
}) {
  if (view === FLORIST_PERFORMANCE_VIEW.ORDERS) return null

  const definitionKey = view
  const titles = {
    customer_sales: 'How payments collected are counted',
    platform_revenue: 'How platform revenue is counted',
    momentum: 'How recent sales trends are calculated',
  }

  return (
    <InfoNotice compact tone="neutral" title={titles[definitionKey]} icon={<Info />}>
      {definitions?.[definitionKey] ?? fallbackDefinition(definitionKey)}
    </InfoNotice>
  )
}

function StatusSelect({
  value,
  onValueChange,
}: {
  value: FloristPerformanceReportFilters['status']
  onValueChange: (status: string) => void
}) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger
        aria-label="Filter florist status"
        selected={value !== FLORIST_PERFORMANCE_STATUS.ALL}
        className="w-fit min-w-[155px]"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {STATUS_OPTIONS.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function MomentumMetricSelect({
  value,
  onValueChange,
}: {
  value: MomentumMetric
  onValueChange: (momentumMetric: string) => void
}) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger
        aria-label="Choose recent sales metric"
        selected
        className="w-fit min-w-[175px]"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {MOMENTUM_OPTIONS.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function getDatePresets(minDate: string): ReportDateRangePreset[] {
  return [
    trailingPreset('trailing_7', 'Last 7 days', 6),
    trailingPreset('trailing_30', 'Last 30 days', 29),
    trailingPreset('trailing_90', 'Last 90 days', 89),
    {
      key: 'all_time',
      label: 'All Time',
      getRange: (now) => ({
        start_date: minDate,
        end_date: now.format('YYYY-MM-DD'),
      }),
    },
  ]
}

function trailingPreset(key: string, label: string, days: number): ReportDateRangePreset {
  return {
    key,
    label,
    getRange: (now) => ({
      start_date: now.subtract(days, 'day').format('YYYY-MM-DD'),
      end_date: now.format('YYYY-MM-DD'),
    }),
  }
}

function viewLabel(view: View) {
  return VIEW_OPTIONS.find((option) => option.value === view)?.label ?? 'Order Count'
}

function tableDescription(filters: FloristPerformanceReportFilters) {
  if (filters.view === FLORIST_PERFORMANCE_VIEW.MOMENTUM) {
    const metric =
      MOMENTUM_OPTIONS.find((option) => option.value === filters.momentum_metric)?.label ?? ''

    return `Average ${metric.toLowerCase()} per calendar day as of ${filters.end_date}, compared with each immediately preceding window.`
  }

  return `Activity from ${filters.start_date} through ${filters.end_date} in the OneStem global timezone, including activity before website Go Live.`
}

function floristCountText(total: number) {
  return `${formatNumber(total)} ${total === 1 ? 'florist' : 'florists'}`
}

function formatMomentumValue(value: number | string | undefined, metric: MomentumMetric) {
  return metric === FLORIST_PERFORMANCE_MOMENTUM_METRIC.ORDERS
    ? formatNumber(value)
    : formatCurrency(value)
}

function fallbackDefinition(key: Exclude<View, 'orders'>) {
  return {
    customer_sales:
      "Money collected toward orders less money returned, counted on the day it was collected in each shop's own days.",
    platform_revenue:
      'Website/POS fees plus v2 Merchant Fee margin after payment processing costs are matched. Matched fees and costs use the original payment date. Merchant Fees shows retained margin separately from the source columns.',
    momentum:
      'Each daily average divides the window total by all 7 or 30 calendar days, including days with no activity. Completed windows end on the As of date and are compared with the immediately preceding windows. New is based on account creation, not website Go Live.',
  }[key]
}
